import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

import { ArrowLeft, Clock, User, Calendar, ChevronRight, Share2, Link as LinkIcon } from 'lucide-react';
import { formatDate } from '../../utils/dateUtils';
import { Lightbox } from '../ui/Lightbox';
import { ShareModal } from '../ui/ShareModal';

// In create-react-app and similar build systems, environment variables are inserted
// at build time, so we can safely reference them like this
// During development these variables are defined in frontend/.env
const IS_DEV = process.env.NODE_ENV !== 'production';

// Helper function to get absolute URL for SEO metadata
const getAbsoluteUrl = (path: string): string => {
  if (!path) return ''; // Add a safety check for undefined path
  const baseUrl = IS_DEV ? 'http://localhost:5176' : 'https://www.cognitivebiaslab.com';
  // Ensure path has a leading slash but no trailing slash
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  // Add trailing slash if not present and it's not the homepage
  const pathWithSlash = normalizedPath === '/' ? normalizedPath : normalizedPath.endsWith('/') ? normalizedPath : `${normalizedPath}/`;
  return `${baseUrl}${pathWithSlash}`;
};

// Helper function to strip HTML tags for meta description
const stripHtml = (html: string): string => {
  if (!html) return ''; // Add a safety check for undefined html
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

// Helper function to extract excerpt for meta description
const getExcerpt = (content: string, maxLength: number = 160): string => {
  if (!content) return ''; // Add a safety check for undefined content
  
  // Strip all HTML tags and Markdown syntax
  const plainText = stripHtml(content).replace(/[#*_~`]/g, '');
  
  // Truncate to maxLength and add ellipsis if needed
  if (plainText.length <= maxLength) return plainText;
  
  // Try to truncate at a sentence or word boundary
  let truncated = plainText.substring(0, maxLength);
  const lastPeriod = truncated.lastIndexOf('.');
  const lastSpace = truncated.lastIndexOf(' ');
  
  if (lastPeriod > maxLength * 0.7) {
    // If there's a period near the end, truncate at the period
    return truncated.substring(0, lastPeriod + 1);
  } else if (lastSpace > 0) {
    // Otherwise truncate at the last space
    return truncated.substring(0, lastSpace) + '...';
  }
  
  return truncated + '...';
};

// Helper function to process image URLs
const getImageUrl = (src: string, blogSlug?: string): string => {
  if (!src) return ''; // Add a safety check for undefined src
  
  // If it's already a full URL, leave it as is
  if (src.startsWith('http://') || src.startsWith('https://')) {
    return src;
  }
  
  // Extract just the filename if a path is provided
  const filename = src.split('/').pop();
  
  // If the image is a relative path and we have a blog slug
  if (filename && blogSlug && !src.startsWith('/')) {
    // Use the slug-specific path
    return `/blog-images/${blogSlug}/${filename}`;
  }
  
  // Fallback to the original path with some cleanup
  let processedSrc = src.startsWith('/') ? src : `/${src}`;
  if (!processedSrc.startsWith('/blog-images/') && !processedSrc.includes('/blog-images/')) {
    processedSrc = `/blog-images${processedSrc}`;
  }
  
  return processedSrc;
};

// More specific processing for featured images
const getFeaturedImageUrl = (src: string, blogSlug?: string): string => {
  if (!src) return ''; // Add a safety check for undefined src
  
  // If it's already a full URL, leave it as is
  if (src.startsWith('http://') || src.startsWith('https://')) {
    return src;
  }
  
  // Try to extract the filename
  const filename = src.split('/').pop();
  
  // If we have a filename and a blog slug, use the slug-specific directory
  if (filename && blogSlug) {
    return `/blog-images/${blogSlug}/${filename}`;
  }
  
  // Fallback to cleaned original path
  let processedSrc = src.startsWith('/') ? src : `/${src}`;
  if (!processedSrc.startsWith('/blog-images/') && !processedSrc.includes('/blog-images/')) {
    processedSrc = `/blog-images${processedSrc}`;
  }
  
  return processedSrc;
};

interface BlogPost {
  _id: string;
  slug: string;
  title: string;
  summary: string;
  content: string;
  featured_image: string;
  author: string;
  published: boolean;
  published_at: string;
  created_at: string;
  updated_at: string;
  meta_description: string;
  keywords: string[];
  tags: string[];
  related_biases: string[];
}

// Utility function to create or update canonical tags to avoid TypeScript errors
const updateCanonical = (url: string, dataSource: string = 'page-component'): void => {
  if (typeof document === 'undefined' || !document.head) return;
  
  // Try to find an existing canonical tag
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  
  // If none exists, create a new one
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  
  // Update properties
  link.href = url;
  
  // Set the data-source attribute
  if (dataSource) {
    link.setAttribute('data-source', dataSource);
  }
};

// Utility function to manage canonical tags based on priority
const manageCanonicalTags = (url: string, defaultSource: string = 'page-component'): void => {
  if (typeof document === 'undefined') return;
  
  // Define priority levels
  const priorityMap: {[key: string]: number} = {
    'page-component': 4,
    'central-head-prerender': 3,
    'post-process': 2,
    'blog-post-prerender': 2,
    'blog-post-api-load': 2,
    'fallback': 1,
    'fallback-plugin': 1
  };
  
  // Get all existing canonical tags
  const canonicalTags = document.querySelectorAll('link[rel="canonical"]');
  
  // If no tags exist, create one and exit
  if (canonicalTags.length === 0) {
    const link = document.createElement('link');
    link.rel = 'canonical';
    link.href = url;
    link.setAttribute('data-source', defaultSource);
    document.head.appendChild(link);
    return;
  }
  
  // Find highest priority tag
  let highestPriority = 0;
  let highestPriorityTag: HTMLLinkElement | null = null;
  
  canonicalTags.forEach(tag => {
    // Handle explicitly as HTMLLinkElement
    const linkTag = tag as HTMLLinkElement;
    const source = linkTag.getAttribute('data-source') || 'fallback';
    const priority = priorityMap[source] || 0;
    
    if (priority > highestPriority) {
      highestPriority = priority;
      highestPriorityTag = linkTag;
    }
  });
  
  // If our new source has higher priority than the existing highest priority tag,
  // we'll need to create a new tag with our URL
  const createNewTag = (!highestPriorityTag || priorityMap[defaultSource] > highestPriority);
  
  // Remove ALL canonical tags - this is crucial to avoid duplicates
  canonicalTags.forEach(tag => {
    tag.parentNode?.removeChild(tag);
  });
  
  // Create a new canonical tag with our URL and source
  const link = document.createElement('link');
  link.rel = 'canonical';
  link.href = url;
  link.setAttribute('data-source', defaultSource);
  link.setAttribute('key', 'canonical'); // Add key attribute for consistency with BiasPage
  document.head.appendChild(link);
};

// Component declaration
const BlogPost: React.FC = () => {
  const { slug } = useParams();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scrollY, setScrollY] = useState<number>(0);
  const [showShareButtons, setShowShareButtons] = useState<boolean>(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState<boolean>(false);
  const [lightboxImageSrc, setLightboxImageSrc] = useState<string>('');
  const [lightboxImageAlt, setLightboxImageAlt] = useState('');
  const [featuredImageError, setFeaturedImageError] = useState<boolean>(false);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);
  const [showCopyNotification, setShowCopyNotification] = useState<boolean>(false);
  const [copyNotificationText, setCopyNotificationText] = useState<string>('Link copied to clipboard!');
  const navigate = useNavigate();

  // Add a flag to detect prerender mode
  const isPrerenderMode = typeof window !== 'undefined' && (
    window.location.href.includes('?prerender') ||
    (window as any).__PRERENDER_INJECTED || 
    (window as any).__PRERENDERED || 
    navigator.userAgent.indexOf('Prerender') !== -1
  );
  
  // Debug element for prerendering (only included in development mode)
  const prerenderDebugElement = IS_DEV ? (
    <div id="prerender-debug" style={{ display: 'none' }}>
      <div id="prerender-slug">{slug}</div>
      <div id="prerender-has-data">{post ? 'true' : 'false'}</div>
    </div>
  ) : null;
  
  useEffect(() => {
    // Detect if we're in server-side rendering or prerendering environment
    const isSSR = typeof window === 'undefined';
    
    // For prerendering, manually inject needed metadata
    if (isSSR || isPrerenderMode) {
      // Server-side code path for prerendering
      if (IS_DEV) console.log('[BlogPost] Prerender mode detected for slug:', slug);
      
      try {
        // IMPORTANT: This is a direct, synchronous solution for prerendering
        // It will allow the data to be included directly in the prerendered HTML
        if (slug === 'best-resources-learning-cognitive-biases') {
          if (IS_DEV) console.log('[BlogPost] Injecting hardcoded data for prerendering');
          
          // Hardcoded data for this specific blog post to ensure it appears during prerendering
          const hardcodedPost = {
            _id: '67e834e2233fa886905248ee',
            slug: 'best-resources-learning-cognitive-biases',
            title: 'The Best Resources for Learning About Cognitive Biases in 2025',
            summary: 'Discover the most effective websites and tools for understanding cognitive biases, with practical advice on creating your personal learning journey.',
            content: `Let's face it—we all think we're pretty rational people. But cognitive biases—those systematic errors in our thinking—affect everyone, including you and me.

In 2025, understanding these biases isn't just academic curiosity; it's a critical skill for making better decisions in our increasingly complex world. Whether you're a student, professional, or simply someone who wants to think more clearly, this guide will help you find the best resources to learn about cognitive biases.

## Interactive Learning Platforms

### 1. Cognitive Bias Explorer
This interactive tool allows you to explore over 150 cognitive biases through engaging scenarios and real-world examples. Each bias includes practical strategies to mitigate its effects.

### 2. Decision Lab
An AI-powered platform that analyzes your decision patterns and identifies which biases might be influencing your thinking in real-time.

### 3. Bias Trainer
This gamified app creates personalized challenges based on your profession and daily activities, helping you recognize and overcome biases that specifically impact your life.

## Comprehensive Reference Materials

### 1. The Cognitive Bias Codex (Updated for 2025)
This beautifully designed visualization organizes nearly 200 cognitive biases into intuitive categories, making the complex landscape of biases easier to navigate and understand.

### 2. Cognitive Bias Lab
Our very own platform offers in-depth articles, practical exercises, and a community forum where you can discuss real-life bias experiences with others.

### 3. The Decision-Maker's Guide to Cognitive Biases
This searchable database connects specific decisions (from career choices to investments) with the biases most likely to affect them.

## How to Create Your Learning Path

Rather than trying to memorize all biases at once (which is overwhelming and ineffective), I recommend this approach:

1. **Start with awareness**: Spend a week simply noticing how biases might be affecting your everyday decisions.

2. **Focus on impact**: Identify which biases most significantly impact your personal and professional life.

3. **Practice deliberately**: Use interactive tools to train your recognition skills in realistic scenarios.

4. **Apply your knowledge**: The true test is using what you've learned to make better real-world decisions.

Remember, the goal isn't to eliminate biases completely—that's impossible for humans—but to develop a healthy awareness that improves your decision-making over time.

## Looking Ahead

As machine learning continues to advance, we're seeing exciting new tools that can help identify our individual bias patterns. However, technology alone can't solve the problem—developing a bias-aware mindset requires consistent practice and reflection.

The best approach combines technological aids with human curiosity and self-awareness. By understanding how your mind works—including its predictable flaws—you'll be better equipped to navigate our increasingly complex world.

What cognitive biases have you noticed in your own thinking? Start your learning journey today!`,
            featured_image: '/blog-images/best-resources-learning-cognitive-biases/Gradient.png',
            author: 'Vladimir Loginov',
            published: true,
            published_at: '2024-03-29T04:40:00.000Z',
            created_at: '2024-03-28T12:40:00.000Z',
            updated_at: '2024-03-29T04:40:00.000Z',
            meta_description: 'Discover the best online resources for learning about cognitive biases in 2025, from interactive tools to comprehensive reference guides.',
            keywords: ['cognitive biases', 'learning resources', 'critical thinking'],
            tags: ['resources', 'learning tools', 'cognitive biases', 'practical guides'],
            related_biases: ['anchoring-bias', 'confirmation-bias']
          };
          setPost(hardcodedPost as BlogPost);
          setLoading(false);
          
          // Following the SEO Canonical Guide priority system
          const canonicalUrl = getAbsoluteUrl(`/blog/${slug}/`);
          
          // Only run DOM operations if we're in a browser environment
          if (typeof document !== 'undefined' && document.head) {
            // First, remove any existing canonical tags
            const existingCanonicalTags = document.querySelectorAll('link[rel="canonical"]');
            existingCanonicalTags.forEach(tag => {
              tag.parentNode?.removeChild(tag);
            });
            
            // Create a new canonical tag with the highest priority
            const link = document.createElement('link');
            link.rel = 'canonical';
            link.href = canonicalUrl;
            link.setAttribute('data-source', 'page-component');
            document.head.appendChild(link);
          }
          
          // Run the priority management to clean up any duplicates
          setTimeout(() => manageCanonicalTags(canonicalUrl, 'page-component'), 50);
          
          // Only run DOM operations if we're in a browser environment
          if (typeof document !== 'undefined' && document.body) {
            // Create a ready indicator for the prerenderer to detect
            const readyIndicator = document.createElement('div');
            readyIndicator.id = 'app-ready-indicator';
            readyIndicator.style.display = 'none';
            document.body.appendChild(readyIndicator);
            
            // Signal completion for prerendering
            setTimeout(() => {
              document.dispatchEvent(new Event('blog-post-loaded'));
              setTimeout(() => {
                // Run another canonical tag check before final render
                manageCanonicalTags(canonicalUrl, 'page-component');
                document.dispatchEvent(new Event('app-fully-rendered'));
              }, 1000);
            }, 100);
          }
          
          return; // Skip the normal API request for prerendering
        }
      } catch (error) {
        console.error('[BlogPost] Error injecting prerender data:', error);
      }
    }

    // For prerendering, inject a canonical link directly if needed
    if (isPrerenderMode && slug) {
      const baseUrl = IS_DEV ? 'http://localhost:5176' : 'https://www.cognitivebiaslab.com';
      const canonicalUrl = `${baseUrl}/blog/${slug}/`;
      
      // Only run DOM operations if we're in a browser environment
      if (typeof document !== 'undefined' && document.head) {
        // First, remove any existing canonical tags
        const existingCanonicalTags = document.querySelectorAll('link[rel="canonical"]');
        existingCanonicalTags.forEach(tag => {
          tag.parentNode?.removeChild(tag);
        });
        
        // Create a new canonical tag with the highest priority
        const link = document.createElement('link');
        link.rel = 'canonical';
        link.href = canonicalUrl;
        link.setAttribute('data-source', 'page-component');
        document.head.appendChild(link);
      }
    }

    // Fetch blog post by slug
    setLoading(true);
    setFeaturedImageError(false);
    setError(null); // Clear any previous errors
    
    let errorTimer: NodeJS.Timeout | null = null;
    
    if (slug) {
      axios.get(`/api/blogs/${slug}`)
        .then(response => {
          // Process the blog content to fix image URLs
          const data = response.data;
          if (data.content) {
            // Replace all markdown image references
            data.content = data.content
              .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (match: string, alt: string, src: string) => {
                return `![${alt}](${getImageUrl(src, slug)})`;
              });
              
            // Replace HTML img tags as well
            data.content = data.content
              .replace(/<img\s+([^>]*)src=["']([^"']+)["']([^>]*)>/g, (match: string, before: string, src: string, after: string) => {
                return `<img ${before}src="${getImageUrl(src, slug)}"${after}>`;
              });
          }
          
          // Also process the featured image with special handling
          if (data.featured_image) {
            data.featured_image = getFeaturedImageUrl(data.featured_image, slug);
          }
          
          setPost(data);
          setLoading(false);
          
          // Clear any pending error timer
          if (errorTimer) clearTimeout(errorTimer);
          
          // After getting the post, fetch related posts
          if (data?.related_biases?.length) {
            fetchRelatedPosts(data.related_biases);
          }
          
          // If in prerender mode, signal that the blog post has been loaded
          if (isPrerenderMode) {
            // Create or update a ready indicator for the prerenderer to detect
            let readyIndicator = document.getElementById('app-ready-indicator');
            if (!readyIndicator) {
              readyIndicator = document.createElement('div');
              readyIndicator.id = 'app-ready-indicator';
              readyIndicator.style.display = 'none';
              document.body.appendChild(readyIndicator);
            }
            
            // Force the canonical URL for this page
            const canonicalUrl = getAbsoluteUrl(`/blog/${slug}/`);
            
            // First, remove any existing canonical tags
            const existingCanonicalTags = document.querySelectorAll('link[rel="canonical"]');
            existingCanonicalTags.forEach(tag => {
              tag.parentNode?.removeChild(tag);
            });
            
            // Create a new canonical tag with the highest priority
            const link = document.createElement('link');
            link.rel = 'canonical';
            link.href = canonicalUrl;
            link.setAttribute('data-source', 'page-component');
            document.head.appendChild(link);
            
            // Signal completion for prerendering
            setTimeout(() => {
              if (IS_DEV) console.log('[BlogPost] API-loaded blog post, dispatching events');
              document.dispatchEvent(new Event('blog-post-loaded'));
              setTimeout(() => {
                document.dispatchEvent(new Event('app-fully-rendered'));
              }, 500);
            }, 100);
          }
        })
        .catch(error => {
          console.error('Error fetching blog post:', error);
          
          // Set a timer before showing the error - allows content to load if it's just slow
          errorTimer = setTimeout(() => {
            setError('Failed to load the blog post. Please try again later.');
            setLoading(false);
          }, 2000); // 2 second delay before showing error
          
          // For prerendering, still signal that we've completed loading
          // so the prerenderer doesn't wait indefinitely
          if (isPrerenderMode) {
            console.error('Error occurred during prerendering blog post:', error.message);
            // Still trigger the events to prevent hanging
            document.dispatchEvent(new Event('blog-post-loaded'));
            
            // Wait a moment before triggering final event
            setTimeout(() => {
              document.dispatchEvent(new Event('app-fully-rendered'));
            }, 500);
          }
        });
    } else {
      // Set a timer before showing the error for missing slug
      errorTimer = setTimeout(() => {
        setError('Invalid blog URL');
        setLoading(false);
      }, 2000); // 2 second delay before showing error
      
      // For prerendering with missing slug, still complete the process
      if (isPrerenderMode) {
        console.error('Prerendering blog post with missing slug');
        document.dispatchEvent(new Event('blog-post-loaded'));
        document.dispatchEvent(new Event('app-fully-rendered'));
      }
    }
    
    return () => {
      // Clean up timer if component unmounts
      if (errorTimer) clearTimeout(errorTimer);
    };
  }, [slug]);

  const fetchRelatedPosts = (relatedBiases: string[]) => {
    // We don't need to show errors to users for this non-critical feature
    // Just silently handle errors, but still log them for debugging
    axios.get(`/api/blogs/related`, { params: { biases: relatedBiases.join(',') } })
      .then(response => {
        // Ensure response.data is an array
        const relatedData = Array.isArray(response.data) ? response.data : [];
        
        // Process featured images for related posts
        const processedRelatedData = relatedData.map((post: any) => ({
          ...post,
          summary: post.summary || getExcerpt(post.content),
        }));
        
        setRelatedPosts(processedRelatedData.filter((relatedPost: BlogPost) => relatedPost.slug !== slug).slice(0, 3));
      })
      .catch(error => {
        // Simply log the error but don't display it to users
        console.error('Error fetching related posts:', error);
        // Set empty related posts to avoid any UI issues
        setRelatedPosts([]);
      });
  };

  // Function to handle featured image error
  const handleFeaturedImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const imgElement = e.currentTarget;
    const originalSrc = imgElement.src;
    
    if (IS_DEV) {
      console.error('Featured image failed to load:', originalSrc);
    }
    
    // Only try fallbacks if not already attempted
    if (!originalSrc.includes('?fallback=')) {
      const filename = originalSrc.split('/').pop()?.split('?')[0];
      
      // Try slug directory if not already trying it
      if (filename && slug && !originalSrc.includes(`/${slug}/`)) {
        // Try slug directory
        imgElement.src = `/blog-images/${slug}/${filename}?fallback=1`;
        if (IS_DEV) console.log('Featured image fallback to slug directory:', imgElement.src);
      } else if (slug) {
        // Last resort - try a known default image
        imgElement.src = `/blog-images/${slug}/Gradient.png?fallback=3`;
        if (IS_DEV) console.log('Featured image fallback to Gradient:', imgElement.src);
        
        // If we've reached this point, set the error state
        setFeaturedImageError(true);
      }
    } else if (originalSrc.includes('?fallback=3')) {
      // If we've tried all fallbacks, just use a fixed gradient
      setFeaturedImageError(true);
    }
  };

  // Function to open the lightbox
  const openLightbox = (src: string, alt: string) => {
    setLightboxImageSrc(src);
    setLightboxImageAlt(alt);
    setIsLightboxOpen(true);
  };

  // Function to close the lightbox
  const closeLightbox = () => {
    setIsLightboxOpen(false);
  };

  // Function to get share text for the blog post
  const getShareText = () => {
    if (!post) return 'Check out this blog post from Cognitive Bias Lab';
    
    return `${post.title}: ${post.summary?.substring(0, 100) || 'Read about cognitive biases and how they affect decision-making'}...`;
  };

  // Function to get share URL for the blog post
  const getShareUrl = () => {
    if (!post) return IS_DEV ? 'http://localhost:5176/blog/' : 'https://www.cognitivebiaslab.com/blog/';
    
    return getAbsoluteUrl(`/blog/${post.slug}/`);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center min-h-screen bg-gradient-to-b from-orange-50 via-pink-50/50 to-orange-50/30">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
        </div>
      );
    }

    if (error || !post) {
      return (
        <div className="min-h-screen bg-gradient-to-b from-orange-50 via-pink-50/50 to-orange-50/30 px-4 py-12">
          <div className="container mx-auto">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h1 className="text-2xl font-bold text-gray-800 mb-4">Cognitive Bias Lab Blog</h1>
              <p className="text-gray-700 mb-6">
                Our blog offers insights on cognitive biases, decision-making science, and practical psychology. 
                Explore articles on how biases affect your thinking and learn strategies to make better decisions.
              </p>
              
              {/* Error message */}
              <div className="bg-red-50 p-4 rounded-lg mb-6 border border-red-200">
                <h2 className="text-xl font-bold text-red-600 mb-2">Blog Post Loading Issue</h2>
                <p className="text-gray-700 mb-4">{error || 'The requested blog post could not be loaded at this time.'}</p>
              </div>
              
              <Link 
                to="/blog/" 
                className="inline-flex items-center text-orange-500 hover:text-orange-700"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Browse All Articles
              </Link>
              
              {/* Add some static recommendations to improve SEO content */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Popular Cognitive Biases</h2>
                <p className="text-gray-700 mb-4">
                  While you're here, explore these common cognitive biases that influence everyday decisions:
                </p>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-6">
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    <Link to="/bias/bias-anchoring/" className="text-orange-600 hover:underline">Anchoring Bias</Link>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    <Link to="/bias/bias-confirmation/" className="text-orange-600 hover:underline">Confirmation Bias</Link>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    <Link to="/bias/bias-availability/" className="text-orange-600 hover:underline">Availability Bias</Link>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    <Link to="/bias/bias-dunning-kruger/" className="text-orange-600 hover:underline">Dunning-Kruger Effect</Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <>
        {prerenderDebugElement}
        
        <Helmet prioritizeSeoTags={true}>
          {/* Basic Meta Tags */}
          {post ? (
            <>
              <title>{post.title ? `${post.title} - Cognitive Bias Lab` : 'Blog Post - Cognitive Bias Lab'}</title>
              <meta name="description" content={post.meta_description || getExcerpt(post.content) || post.summary || ''} />
              <meta name="keywords" content={post.keywords?.join(', ') || ''} />
              <meta name="author" content={post.author || 'Cognitive Bias Lab'} />

              {/* Canonical URL - very important for SEO */}
              <link rel="canonical" href={getAbsoluteUrl(`/blog/${post.slug || ''}/`)} data-source="page-component" key="canonical" />

              {/* Open Graph Tags for Social Media */}
              <meta property="og:title" content={post.title || 'Blog Post - Cognitive Bias Lab'} />
              <meta property="og:description" content={post.meta_description || getExcerpt(post.content) || post.summary || ''} />
              <meta property="og:image" content={getAbsoluteUrl(post.featured_image || '')} />
              <meta property="og:type" content="article" />
              <meta property="og:url" content={getAbsoluteUrl(`/blog/${post.slug || ''}/`)} />
              <meta property="og:site_name" content="Cognitive Bias Lab" />
              {post.published_at && <meta property="article:published_time" content={new Date(post.published_at).toISOString()} />}
              {(post.updated_at || post.published_at) && <meta property="article:modified_time" content={new Date(post.updated_at || post.published_at).toISOString()} />}
              <meta property="article:author" content={post.author || 'Cognitive Bias Lab'} />
              <meta property="article:section" content="Cognitive Psychology" />
              {post.tags && Array.isArray(post.tags) && post.tags.map((tag, index) => (
                <meta key={`og:tag:${index}`} property="article:tag" content={tag} />
              ))}

              {/* Twitter Card Tags */}
              <meta name="twitter:card" content="summary_large_image" />
              <meta name="twitter:title" content={post.title || 'Blog Post - Cognitive Bias Lab'} />
              <meta name="twitter:description" content={post.meta_description || getExcerpt(post.content) || post.summary || ''} />
              <meta name="twitter:image" content={getAbsoluteUrl(post.featured_image || '')} />
              
              {/* JSON-LD Structured Data for better search results */}
              <script type="application/ld+json">
                {JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "BlogPosting",
                  "headline": post.title || 'Blog Post - Cognitive Bias Lab',
                  "image": getAbsoluteUrl(post.featured_image || ''),
                  "datePublished": post.published_at ? new Date(post.published_at).toISOString() : new Date().toISOString(),
                  "dateModified": post.updated_at || post.published_at ? new Date(post.updated_at || post.published_at).toISOString() : new Date().toISOString(),
                  "author": {
                    "@type": "Person",
                    "name": post.author || 'Cognitive Bias Lab'
                  },
                  "publisher": {
                    "@type": "Organization",
                    "name": "Cognitive Bias Lab",
                    "logo": {
                      "@type": "ImageObject",
                      "url": getAbsoluteUrl("/images/logo.png")
                    }
                  },
                  "description": post.meta_description || post.summary || '',
                  "keywords": post.keywords?.join(', ') || '',
                  "mainEntityOfPage": {
                    "@type": "WebPage",
                    "@id": getAbsoluteUrl(`/blog/${post.slug || ''}/`)
                  }
                })}
              </script>
            </>
          ) : (
            <>
              <title>Blog Post - Cognitive Bias Lab</title>
              <meta name="description" content="Explore the fascinating world of cognitive biases and how they affect our everyday decisions." />
              {/* Add fallback canonical for prerendering */}
              {slug && <link rel="canonical" href={getAbsoluteUrl(`/blog/${slug}/`)} data-source="page-component" key="canonical" />}
              <meta property="og:title" content="Blog Post - Cognitive Bias Lab" />
              <meta property="og:description" content="Explore the fascinating world of cognitive biases and how they affect our everyday decisions." />
              <meta property="og:type" content="article" />
              <meta property="og:url" content={slug ? getAbsoluteUrl(`/blog/${slug}/`) : getAbsoluteUrl('/blog/')} />
              <meta property="og:site_name" content="Cognitive Bias Lab" />
              {/* Removed noindex directive to ensure Google can index the page even during loading */}
              <meta name="robots" content="index,follow" />
            </>
          )}
        </Helmet>

        <Lightbox 
          isOpen={isLightboxOpen}
          onClose={closeLightbox}
          imageSrc={lightboxImageSrc}
          alt={lightboxImageAlt || 'Blog image'}
          title={lightboxImageAlt}
        />

        {/* Share Modal */}
        <ShareModal 
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          shareUrl={getShareUrl()}
          shareText={getShareText()}
          title={`Share: ${post?.title || 'Blog Post'}`}
        />

        <div className="container mx-auto px-4 py-12">
          {/* Back Navigation */}
          <div className="mb-8">
            <Link 
              to="/blog/" 
              className="inline-flex items-center text-orange-500 hover:text-orange-700 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to All Articles
            </Link>
          </div>

          {/* Loading, Error, or Content States */}
          {loading ? (
            <div className="bg-white rounded-xl shadow-lg p-8">
              {/* Enhanced skeleton with more specific content for crawlers */}
              <div className="animate-pulse flex flex-col">
                {/* Featured image skeleton */}
                <div className="rounded-xl bg-gray-200 h-64 w-full mb-8"></div>
                
                {/* Title and meta skeleton */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="inline-block bg-gray-200 h-6 w-20 rounded-full"></span>
                  <span className="inline-block bg-gray-200 h-6 w-24 rounded-full"></span>
                </div>
                
                <div className="h-8 bg-gray-200 rounded w-3/4 mb-6"></div>
                
                {/* Author and date skeleton */}
                <div className="flex flex-wrap gap-4 mb-6">
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-gray-200 rounded-full mr-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-gray-200 rounded-full mr-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                  </div>
                </div>
                
                {/* Summary skeleton */}
                <div className="h-6 bg-gray-200 rounded w-full mb-2"></div>
                <div className="h-6 bg-gray-200 rounded w-5/6 mb-2"></div>
                <div className="h-6 bg-gray-200 rounded w-4/6 mb-6"></div>
                
                <hr className="border-gray-200 mb-8" />
                
                {/* Content skeleton */}
                <div className="space-y-4">
                  <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-6"></div>
                  
                  <div className="h-48 bg-gray-200 rounded w-full mb-6"></div>
                  
                  <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6 mb-2"></div>
                </div>
              </div>
              
              {/* Keep a hidden H1 with meaningful content for search engines */}
              <h1 className="sr-only">Blog Post - Cognitive Bias Lab</h1>
              <p className="sr-only">This article explores cognitive biases and how they affect decision making. Content is loading...</p>
              <p className="text-gray-500 mt-8 text-center">Loading article...</p>
            </div>
          ) : error ? (
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <div className="text-red-500 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold mb-2">Error Loading Blog Post</h2>
              <p className="text-gray-600">{error}</p>
              <button 
                onClick={() => window.location.reload()} 
                className="mt-6 px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : post ? (
            <>
              {/* Featured Image */}
              <div className="relative w-full rounded-xl overflow-hidden mb-8">
                {featuredImageError ? (
                  /* Fallback gradient background when image fails to load */
                  <div 
                    className="w-full h-64 md:h-96 bg-gradient-to-r from-orange-400 via-pink-500 to-purple-600 rounded-xl flex items-center justify-center"
                  >
                    <h1 className="text-3xl md:text-4xl lg:text-5xl text-white font-bold text-center px-6 drop-shadow-sm">
                      {post.title}
                    </h1>
                  </div>
                ) : (
                  /* Regular featured image with alt text */
                  <img 
                    src={post.featured_image}
                    alt={post.title} 
                    className="w-full h-auto rounded-xl object-cover max-h-[400px] cursor-pointer hover:opacity-95 transition-opacity"
                    onClick={() => openLightbox(post.featured_image, post.title)}
                    onError={handleFeaturedImageError}
                  />
                )}
              </div>

              <article className="bg-white rounded-xl shadow-lg overflow-hidden">
                {/* Header Section */}
                <div className="p-8 pb-4">
                  <div className="flex flex-wrap gap-2 mb-4">
                    {post.tags && Array.isArray(post.tags) && post.tags.map((tag, index) => (
                      <span 
                        key={index} 
                        className="inline-block bg-orange-100 text-orange-800 text-xs px-3 py-1 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  <h1 className="text-3xl md:text-4xl mb-6 font-bold text-gray-800">
                    {post.title}
                  </h1>
                  
                  <div className="flex flex-wrap items-center text-gray-600 mb-6 gap-4">
                    <div className="flex items-center">
                      <User className="w-4 h-4 mr-2" />
                      <span className="text-base">{post.author}</span>
                    </div>
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-2" />
                      <span className="text-base">{formatDate(new Date(post.published_at))}</span>
                    </div>
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-2" />
                      <span className="text-base">{post.content ? Math.ceil(post.content.split(' ').length / 200) : 5} min read</span>
                    </div>
                    <div className="flex items-center cursor-pointer text-orange-500 hover:text-orange-600 transition-colors" onClick={() => setIsShareModalOpen(true)}>
                      <Share2 className="w-4 h-4 mr-2" />
                      <span className="text-base">Share</span>
                    </div>
                  </div>

                  <p className="text-gray-700 text-lg mb-6">
                    {post.summary}
                  </p>

                  <hr className="border-gray-200 mb-8" />
                </div>

                {/* Blog Content */}
                <div className="px-8 pb-8 prose prose-base max-w-none prose-headings:text-gray-900 prose-headings:font-bold prose-p:text-gray-700 prose-a:text-orange-600 prose-a:no-underline hover:prose-a:underline prose-img:rounded-lg">
                  {/* Enhanced ReactMarkdown with rehype-raw plugin */}
                  <ReactMarkdown 
                    rehypePlugins={[rehypeRaw]}
                    components={{
                      img: ({ node, alt, src, ...props }) => {
                        // Use the helper function for image URL processing
                        const imageSrc = src ? getImageUrl(src, slug) : '';
                        
                        // Extract width and height attributes from props if they exist
                        const { width, height, ...otherProps } = props as any;
                        
                        // Check if the image has a width or height defined in the markdown
                        const hasExplicitSize = width || height;
                        
                        // Return just the image element without a wrapper div to avoid DOM nesting issues
                        return (
                          <img 
                            src={imageSrc} 
                            alt={alt || 'Blog image'} 
                            className={`rounded-lg border border-gray-200 mx-auto block my-6 ${hasExplicitSize ? '' : 'max-w-full md:max-w-[600px] lg:max-w-[800px]'} cursor-pointer hover:opacity-95 transition-opacity`}
                            style={{
                              maxHeight: '600px',
                              objectFit: 'contain',
                              width: width ? width : 'auto',
                              height: height ? height : 'auto',
                            }}
                            onClick={() => openLightbox(imageSrc, alt || 'Blog image')}
                            onError={(e) => {
                              // If an image fails to load, try a fallback approach
                              const imgElement = e.currentTarget;
                              const originalSrc = imgElement.src;
                              
                              // Only attempt fallback if not already attempted
                              if (!originalSrc.includes('?fallback=true')) {
                                // Try different paths in order
                                const filename = originalSrc.split('/').pop()?.split('?')[0];
                                if (filename && slug) {
                                  // Try slug-specific path
                                  imgElement.src = `/blog-images/${slug}/${filename}?fallback=1`;
                                  if (IS_DEV) console.log('Image fallback to slug path:', { from: originalSrc, to: imgElement.src });
                                } else if (IS_DEV && !originalSrc.includes('localhost:3001')) {
                                  // Last resort - try backend direct URL
                                  const backendUrl = originalSrc.replace(window.location.origin, 'http://localhost:3001');
                                  imgElement.src = `${backendUrl}?fallback=3`;
                                  if (IS_DEV) console.log('Image fallback to backend:', { from: originalSrc, to: imgElement.src });
                                }
                              }
                            }}
                            {...otherProps}
                          />
                        );
                      }
                    }}
                  >
                    {post.content}
                  </ReactMarkdown>

                  {/* Share Section with Social Icons */}
                  <div className="mt-12 border-t border-gray-200 pt-8">
                    <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-4 flex items-center">
                      <Share2 className="w-6 h-6 mr-3 text-orange-500" />
                      <span>Share this article with your network</span>
                    </h3>
                    <div className="flex flex-wrap gap-3">
                      {/* LinkedIn */}
                      <button 
                        onClick={() => {
                          const url = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(getShareUrl())}&title=${encodeURIComponent(post.title)}&summary=${encodeURIComponent(post.summary || '')}`;
                          window.open(url, '_blank');
                        }}
                        className="p-3 bg-[#0A66C2] text-white rounded-full hover:bg-[#004182] transition-colors"
                        aria-label="Share on LinkedIn"
                      >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                        </svg>
                      </button>
                      
                      {/* X (formerly Twitter) */}
                      <button 
                        onClick={() => {
                          const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(getShareText())}&url=${encodeURIComponent(getShareUrl())}`;
                          window.open(url, '_blank');
                        }}
                        className="p-3 bg-black text-white rounded-full hover:bg-gray-800 transition-colors"
                        aria-label="Share on X (Twitter)"
                      >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                        </svg>
                      </button>
                      
                      {/* Facebook */}
                      <button 
                        onClick={() => {
                          const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getShareUrl())}`;
                          window.open(url, '_blank');
                        }}
                        className="p-3 bg-[#1877F2] text-white rounded-full hover:bg-[#0d65d9] transition-colors"
                        aria-label="Share on Facebook"
                      >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                        </svg>
                      </button>
                      
                      {/* Instagram - Open Instagram website */}
                      <button 
                        onClick={() => {
                          // While Instagram doesn't have a direct web sharing API like other platforms,
                          // we can at least open Instagram's website
                          window.open('https://www.instagram.com/', '_blank');
                        }}
                        className="p-3 bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#FCAF45] text-white rounded-full hover:opacity-90 transition-opacity"
                        aria-label="Share on Instagram"
                      >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                        </svg>
                      </button>
                      
                      {/* WhatsApp */}
                      <button 
                        onClick={() => {
                          // Using the WhatsApp web API to pre-fill text and URL
                          const url = `https://wa.me/?text=${encodeURIComponent(getShareText() + ' ' + getShareUrl())}`;
                          window.open(url, '_blank');
                        }}
                        className="p-3 bg-[#25D366] text-white rounded-full hover:bg-[#128C7E] transition-colors"
                        aria-label="Share on WhatsApp"
                      >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                          <path fillRule="evenodd" clipRule="evenodd" d="M20.52 3.449C18.24 1.245 15.24 0.05 12.045 0.05C5.463 0.05 0.1 5.413 0.1 11.995C0.1 14.11 0.6 16.17 1.55 18.01L0 24.05L6.2 22.535C7.95 23.395 9.85 23.855 11.795 23.855C18.425 23.855 23.745 18.49 23.745 11.91C23.75 8.795 22.575 5.844 20.505 3.452L20.52 3.449ZM12.05 21.839C10.2 21.839 8.4 21.395 6.85 20.535L6.5 20.334L2.875 21.165L3.69 17.649L3.465 17.284C2.53 15.699 2.029 13.936 2.029 12.116C2.029 6.489 6.432 1.95 12.075 1.95C14.734 1.95 17.215 2.975 19.08 4.765C20.94 6.605 21.925 9.045 21.93 11.66C21.93 17.286 17.445 21.835 12.05 21.835V21.839ZM17.595 14.471C17.3 14.325 15.825 13.61 15.565 13.504C15.3 13.405 15.115 13.354 14.93 13.655C14.75 13.95 14.175 14.625 14.011 14.81C13.845 14.996 13.685 15.021 13.39 14.879C13.1 14.731 12.1 14.39 10.94 13.356C10.035 12.541 9.4 11.556 9.235 11.255C9.07 10.955 9.22 10.804 9.36 10.664C9.495 10.54 9.65 10.35 9.795 10.185C9.945 10.02 9.99 9.895 10.09 9.715C10.19 9.535 10.145 9.369 10.07 9.22C9.99 9.075 9.365 7.595 9.125 7.005C8.895 6.433 8.658 6.509 8.484 6.499C8.319 6.49 8.135 6.486 7.95 6.486C7.765 6.486 7.455 6.565 7.195 6.86C6.935 7.154 6.17 7.869 6.17 9.3C6.17 10.73 7.195 12.104 7.34 12.285C7.49 12.469 9.365 15.316 12.171 16.581C12.981 16.931 13.613 17.152 14.101 17.315C14.901 17.591 15.635 17.554 16.215 17.472C16.865 17.388 18.073 16.754 18.315 16.066C18.555 15.378 18.555 14.786 18.48 14.665C18.405 14.544 18.225 14.471 17.945 14.325L17.595 14.471Z" />
                        </svg>
                      </button>
                      
                      {/* Telegram */}
                      <button 
                        onClick={() => {
                          const url = `https://t.me/share/url?url=${encodeURIComponent(getShareUrl())}&text=${encodeURIComponent(getShareText())}`;
                          window.open(url, '_blank');
                        }}
                        className="p-3 bg-[#0088cc] text-white rounded-full hover:bg-[#0077b3] transition-colors"
                        aria-label="Share on Telegram"
                      >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.96 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                        </svg>
                      </button>
                      
                      {/* Copy Link Button - Only this button shows the notification */}
                      <button 
                        onClick={() => {
                          navigator.clipboard.writeText(getShareUrl())
                            .then(() => {
                              setCopyNotificationText('Link copied to clipboard!');
                              setShowCopyNotification(true);
                              setTimeout(() => {
                                setShowCopyNotification(false);
                              }, 3000);
                            })
                            .catch(err => {
                              console.error('Failed to copy URL: ', err);
                            });
                        }}
                        className="p-3 bg-gray-700 hover:bg-gray-800 text-white rounded-full transition-colors flex items-center gap-1 px-4"
                        aria-label="Copy link to clipboard"
                      >
                        <LinkIcon className="w-5 h-5" />
                        <span className="text-sm hidden sm:inline">Copy Link</span>
                      </button>
                    </div>
                  </div>
                </div>
              </article>

              {/* Related Posts */}
              {relatedPosts.length > 0 && (
                <div className="mt-12">
                  <h2 className="text-2xl font-bold mb-6">Related Articles</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {relatedPosts.map((relatedPost) => (
                      <Link 
                        key={relatedPost._id}
                        to={`/blog/${relatedPost.slug}/`}
                        className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300"
                      >
                        <div className="h-40 overflow-hidden" onClick={(e) => {
                          e.preventDefault();
                          openLightbox(relatedPost.featured_image, relatedPost.title);
                        }}>
                          <img 
                            src={relatedPost.featured_image} 
                            alt={relatedPost.title} 
                            className="w-full h-full object-cover cursor-pointer hover:opacity-95 transition-opacity"
                          />
                        </div>
                        <div className="p-4">
                          <h3 className="font-bold text-lg mb-2 line-clamp-2">{relatedPost.title}</h3>
                          <p className="text-gray-600 text-base line-clamp-2">{relatedPost.summary}</p>
                          <div className="flex items-center justify-between mt-4 text-sm">
                            <span className="text-gray-500">{formatDate(new Date(relatedPost.published_at))}</span>
                            <span className="text-orange-500 flex items-center">
                              Read More <ChevronRight className="w-4 h-4 ml-1" />
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <h2 className="text-xl font-bold mb-2">Blog Post Not Found</h2>
              <p className="text-gray-600">The requested blog post could not be loaded.</p>
              <Link 
                to="/blog/" 
                className="mt-6 inline-block px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
              >
                Browse All Articles
              </Link>
            </div>
          )}
        </div>

        {/* Floating notification for copy action */}
        {showCopyNotification && (
          <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white py-3 px-5 rounded-lg shadow-lg z-50 animate-fade-in-up">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-green-400 mr-3" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                <path d="M5 13l4 4L19 7"></path>
              </svg>
              <span className="text-sm font-medium">{copyNotificationText}</span>
            </div>
          </div>
        )}
      </>
    );
  };

  return renderContent();
};

export default BlogPost; 